import httpClient from './httpClient';
import { buildQueryString } from '../utils/queryBuilder';
import { parseApiError } from '../utils/errorHandler';
import { BaseService } from './baseService';

class EmployeeService extends BaseService {
    constructor() {
        super('/api/funcionarios');
    }

    /**
     * Retorna o perfil do funcionário logado.
     * GET /api/funcionarios/perfil
     * @returns {Promise<Object>}
     */
    async getProfile() {
        return super.get('perfil');
    }

    /**
     * Lista funcionários com filtros específicos
     * @param {Object} params - Parâmetros da listagem
     * @param {string|number} params.customerId - ID do cliente (obrigatório)
     * @param {number} params.page - Número da página
     * @param {number} params.size - Tamanho da página
     * @param {string} params.sort - Ordenação
     * @returns {Promise<Object>} Resposta da API
     */
    async list({ customerId, page = 0, size = 10, sort, ...otherFilters } = {}) {
        if (!customerId) {
            throw new Error('customerId is required');
        }

        const filters = {
            customerId,
            ...otherFilters,
        };

        return super.list({ page, size, sort, filters });
    }

    /**
     * Busca um funcionário por ID
     * @param {string|number} id - ID do funcionário
     * @param {string|number} customerId - ID do cliente
     * @returns {Promise<Object>} Dados do funcionário
     */
    async get(id, customerId) {
        return super.get(id, { customerId });
    }

    /**
     * Cria um novo funcionário
     * @param {Object} data - Dados do funcionário (deve incluir customerId)
     * @returns {Promise<Response>} Resposta da API
     */
    async create(data) {
        if (!data.customerId) {
            throw new Error('customerId is required');
        }
        return super.create(data);
    }

    /**
     * Atualiza um funcionário existente
     * A API de funcionários usa PUT sem ID na URL, então sobrescrevemos o método
     * @param {string|number} id - ID do funcionário (não usado na URL, mas mantido para compatibilidade)
     * @param {Object} data - Dados atualizados (deve incluir o ID e customerId)
     * @returns {Promise<Response>} Resposta da API
     */
    async update(id, data) {
        // A API de funcionários espera o ID no body, não na URL
        return this.updateWithoutId(data);
    }

    /**
     * Deleta um funcionário
     * @param {string|number} id - ID do funcionário
     * @param {string|number} customerId - ID do cliente
     * @returns {Promise<Response>} Resposta da API
     */
    async delete(id, customerId) {
        return super.delete(id, { customerId });
    }

    /**
     * Alterna o status (ativo/inativo) de um funcionário
     * @param {string|number} id - ID do funcionário
     * @param {string|number} customerId - ID do cliente
     * @param {boolean} active - Novo status do funcionário
     * @returns {Promise<Response>} Resposta da API
     */
    async toggleStatus(id, customerId, active) {
        return super.patch(id, 'removed?customerId=' + customerId + '&removed=' + !active);
    }

    /**
     * Lista solicitantes (para criação de entrega).
     * GET /api/funcionarios/solicitantes
     * @param {Object} params
     * @param {number|string} [params.customerId] - ID do cliente (opcional)
     * @param {number} [params.page=0]
     * @param {number} [params.size=10]
     * @param {string} [params.sort='name,asc']
     * @returns {Promise<Object>} Resposta paginada (content, page)
     */
    async getSolicitantes({ customerId, page = 0, size = 10, sort = 'name,asc' } = {}) {
        const filters = customerId != null ? { customerId } : {};
        const queryString = buildQueryString({ page, size, sort, filters });
        const response = await httpClient.get(`${this.endpoint}/solicitantes?${queryString}`);
        if (!response.ok) {
            throw await parseApiError(response);
        }
        return response.json();
    }
}

const employeeService = new EmployeeService();
export default employeeService;
